import { aiOpenapiClient } from '@/api/openapi-client';
import { env } from '@/env';
import { useCanUseAi } from '@/features/ai-assistant/hooks/useCanUseAi';
import {
  useAiChangeset,
  type AiChangesetAdapter,
} from '@/features/ai-changeset/hooks/useAiChangeset';
import { changesetApi } from '@/features/ai-changeset/utils/changeset-api';
import { lastDraft } from '@/features/ai-changeset/utils/last-draft';
import { buildProductDraft, productChangeLabels } from '@/features/menu/utils/menu-draft';
import { summarizeProductDraft } from '@/features/menu/utils/menu-draft-summary';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { MenuChange, Product } from '@restaurant/api-contracts';
import {
  AiChangesetAppliedSchema,
  MenuChangesetStaleSchema,
  MenuChangesSchema,
  MenuQuickEditSessionSchema,
} from '@restaurant/api-contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

export function useProductQuickEdit(
  menuId: string,
  productId: string,
  product: Product | undefined,
) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const canUseAi = useCanUseAi();
  const summaryCopy = useT().menuQuickEdit.summary;
  const qc = useQueryClient();
  const [draftOf] = useState(() => lastDraft(buildProductDraft));
  const params = { restaurantId, menuId, productId };

  const adapter = useMemo<AiChangesetAdapter<MenuChange>>(
    () => ({
      targets: (changes) => (product ? draftOf(product, changes).targets : []),
      labels: (changes) => (product ? productChangeLabels(product, changes) : []),
      summary: (changes) => summarizeProductDraft(changes, summaryCopy, currency),
    }),
    [product, draftOf, summaryCopy, currency],
  );

  const quickEdit = useAiChangeset<MenuChange>({
    changesSchema: MenuChangesSchema,
    id: `product:${restaurantId}:${menuId}:${productId}`,
    enabled: canUseAi && !!restaurantId && !!product,
    turnUrl: `${env.aiUrl}/restaurants/${restaurantId}/ai/menus/${menuId}/products/${productId}/quick-edit`,
    pageName: 'product',
    fullViewLabel: null,
    viewRows: 1,
    api: changesetApi<MenuChange, unknown>(
      {
        sessionSchema: MenuQuickEditSessionSchema,
        staleSchema: MenuChangesetStaleSchema,
        appliedSchema: AiChangesetAppliedSchema,
      },
      {
        load: (before) =>
          aiOpenapiClient.GET(
            '/restaurants/{restaurantId}/ai/menus/{menuId}/products/{productId}/quick-edit',
            { params: { path: params, query: { before } } },
          ),
        apply: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/menus/{menuId}/products/{productId}/quick-edit/turns/{turnId}/apply',
            { params: { path: { ...params, turnId } } },
          ),
        undo: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/menus/{menuId}/products/{productId}/quick-edit/turns/{turnId}/undo',
            { params: { path: { ...params, turnId } } },
          ),
        discard: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/menus/{menuId}/products/{productId}/quick-edit/turns/{turnId}/discard',
            { params: { path: { ...params, turnId } } },
          ),
      },
    ),
    onApplySettled: () => qc.invalidateQueries({ queryKey: ['menu'] }),
    adapter,
  });

  return { ...quickEdit, draft: product ? draftOf(product, quickEdit.changes) : null };
}

export type ProductQuickEdit = ReturnType<typeof useProductQuickEdit>;
